import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUpdateJobStatus } from '@/hooks/useJobs';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { pickedDayKey, dayKeyToPickerDate } from '@/utils/timezone';
import type { JobWithRelations } from '@/types';

interface CancelJobDialogProps {
  job: JobWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

const cancelSchema = z.object({
  reason: z.string().min(1, 'Please select a cancellation reason'),
  notes: z.string().optional(),
  closeDate: z.date(),
});

type CancelFormData = z.infer<typeof cancelSchema>;

const CANCEL_REASONS = [
  { value: 'tech_cancel', label: 'Tech Cancelled' },
  { value: 'customer_cancel', label: 'Customer Called to Cancel' },
  { value: 'late', label: 'Late' },
  { value: 'other', label: 'Other' },
];

export function CancelJobDialog({
  job,
  open,
  onOpenChange,
  onComplete,
}: CancelJobDialogProps) {
  const updateStatus = useUpdateJobStatus();
  const { formatDateInput } = useTimezoneUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // "Today" and "the future" are the company's calendar days, not the browser's.
  const todayKey = formatDateInput(new Date());

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm<CancelFormData>({
    resolver: zodResolver(cancelSchema.refine(
      (data) => pickedDayKey(data.closeDate) <= todayKey,
      { message: 'Cancellation date cannot be in the future', path: ['closeDate'] },
    )),
    defaultValues: {
      reason: '',
      notes: '',
      closeDate: dayKeyToPickerDate(todayKey),
    },
  });

  const selectedReason = watch('reason');

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = async (data: CancelFormData) => {
    setIsSubmitting(true);
    
    try {
      const reasonLabel = CANCEL_REASONS.find(r => r.value === data.reason)?.label || data.reason;
      const cancellationReason = data.notes 
        ? `${reasonLabel}: ${data.notes}`
        : reasonLabel;

      await updateStatus.mutateAsync({
        jobId: job.id,
        status: 'cancelled',
        cancellationReason,
        closeDate: data.closeDate,
      });

      onComplete();
      handleClose();
    } catch (error) {
      console.error('Error cancelling job:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Job</DialogTitle>
          <DialogDescription>
            Please provide a reason for cancelling this job. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="closeDate">Cancellation Date *</Label>
            <p className="text-xs text-muted-foreground">The actual date the job was cancelled</p>
            <Controller
              name="closeDate"
              control={control}
              render={({ field }) => (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => pickedDayKey(date) > todayKey}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.closeDate && (
              <p className="text-sm text-destructive">{errors.closeDate.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Cancellation Reason *</Label>
            <Select
              value={selectedReason}
              onValueChange={(value) => setValue('reason', value)}
            >
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.reason && (
              <p className="text-sm text-destructive">{errors.reason.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Add any additional details..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel Job'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
